import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useLanguage } from '../contexts/LanguageContext';
import { getReadinessScore } from '../lib/api';
import { Battery, BatteryLow, BatteryMedium, BatteryFull, BatteryCharging, Zap, Moon, Dumbbell, Flame, ChevronRight, Info } from 'lucide-react';

// Nanouk images for different states
const NANOUK_STATES = {
    peak: 'https://images.unsplash.com/photo-1767381604151-bae00f2fb337?w=200https://images.unsplash.com/photo-1767381604151-bae00f2fb337?w=200&h=200&fit=crop', // Trailblazer
    good: 'https://images.unsplash.com/photo-1767381604151-bae00f2fb337?w=200https://images.unsplash.com/photo-1767381604151-bae00f2fb337?w=200&h=200&fit=crop', // Happy
    moderate: 'https://images.unsplash.com/photo-1767381604151-bae00f2fb337?w=200https://images.unsplash.com/photo-1767381604151-bae00f2fb337?w=200&h=200&fit=crop', // Newcomer
    low: 'https://images.unsplash.com/photo-1767381604151-bae00f2fb337?w=200https://images.unsplash.com/photo-1767381604151-bae00f2fb337?w=200&h=200&fit=crop', // Sleeping
    rest: 'https://images.unsplash.com/photo-1767381604151-bae00f2fb337?w=200https://images.unsplash.com/photo-1767381604151-bae00f2fb337?w=200&h=200&fit=crop', // Puppy sleeping
};

const STATUS_CONFIG = {
    peak: { 
        color: 'text-green-500', 
        bgColor: 'bg-green-500/10', 
        borderColor: 'border-green-500/30',
        gradient: 'from-green-500 to-emerald-500',
        icon: BatteryCharging,
        label: { de: 'Topform', en: 'Peak Form' }
    },
    good: { 
        color: 'text-lime-500', 
        bgColor: 'bg-lime-500/10', 
        borderColor: 'border-lime-500/30',
        gradient: 'from-lime-500 to-green-500',
        icon: BatteryFull,
        label: { de: 'Gut', en: 'Good' }
    },
    moderate: { 
        color: 'text-yellow-500', 
        bgColor: 'bg-yellow-500/10', 
        borderColor: 'border-yellow-500/30',
        gradient: 'from-yellow-500 to-amber-500',
        icon: BatteryMedium,
        label: { de: 'Moderat', en: 'Moderate' }
    },
    low: { 
        color: 'text-orange-500', 
        bgColor: 'bg-orange-500/10', 
        borderColor: 'border-orange-500/30',
        gradient: 'from-orange-500 to-red-500',
        icon: BatteryLow,
        label: { de: 'Niedrig', en: 'Low' }
    },
    rest: { 
        color: 'text-red-500', 
        bgColor: 'bg-red-500/10', 
        borderColor: 'border-red-500/30',
        gradient: 'from-red-500 to-pink-500',
        icon: Battery,
        label: { de: 'Ruhetag', en: 'Rest Day' }
    },
};

const content = {
    de: {
        title: 'Readiness-Score',
        subtitle: 'Dein Bereitschafts-Index',
        recommendation: 'Empfehlung',
        breakdown: 'Aufschlüsselung',
        sleepScore: 'Schlaf-Score',
        activityScore: 'Aktivitäts-Score',
        consistencyScore: 'Konstanz-Score',
        recoveryPenalty: 'Erholungs-Abzug',
        viewDetails: 'Details anzeigen',
        basedOn: 'Basierend auf den letzten 3 Tagen',
        whatIsTitle: 'Was ist der Readiness-Score?',
        whatIsDescription: 'Der Readiness-Score zeigt dir, wie bereit dein Körper heute für Aktivität ist. Er basiert auf wissenschaftlichen Erkenntnissen über Erholung und Leistungsfähigkeit.',
        howCalculated: 'So wird er berechnet',
        sleepExplain: 'Schlafqualität & Dauer der letzten Nächte',
        activityExplain: 'Trainingsbelastung vs. Erholung',
        consistencyExplain: 'Regelmäßigkeit deiner Routinen',
        recoveryExplain: 'Abzug bei zu wenig Erholung',
        statusMeanings: 'Was bedeuten die Stufen?',
        peakMeaning: '85-100%: Dein Körper ist optimal erholt. Perfekt für intensive Workouts!',
        goodMeaning: '70-84%: Gute Basis. Normales Training ist ideal.',
        moderateMeaning: '50-69%: Leichte Aktivität empfohlen. Höre auf deinen Körper.',
        lowMeaning: '30-49%: Ruhe und Erholung priorisieren. Yoga oder Spaziergang.',
        restMeaning: '0-29%: Ruhetag! Dein Körper braucht Erholung.',
        proTip: 'Profi-Tipp',
        proTipText: 'Tracke regelmäßig Schlaf und Workouts für genauere Vorhersagen. Je mehr Daten, desto besser versteht Nanouk deinen Körper!',
    },
    en: {
        title: 'Readiness Score',
        subtitle: 'Your Readiness Index',
        recommendation: 'Recommendation',
        breakdown: 'Breakdown',
        sleepScore: 'Sleep Score',
        activityScore: 'Activity Score',
        consistencyScore: 'Consistency Score',
        recoveryPenalty: 'Recovery Penalty',
        viewDetails: 'View Details',
        basedOn: 'Based on the last 3 days',
        whatIsTitle: 'What is the Readiness Score?',
        whatIsDescription: 'The Readiness Score shows how ready your body is for activity today. It\'s based on scientific research about recovery and performance.',
        howCalculated: 'How it\'s calculated',
        sleepExplain: 'Sleep quality & duration of recent nights',
        activityExplain: 'Training load vs. recovery',
        consistencyExplain: 'Regularity of your routines',
        recoveryExplain: 'Penalty for insufficient recovery',
        statusMeanings: 'What do the levels mean?',
        peakMeaning: '85-100%: Your body is optimally recovered. Perfect for intense workouts!',
        goodMeaning: '70-84%: Good foundation. Normal training is ideal.',
        moderateMeaning: '50-69%: Light activity recommended. Listen to your body.',
        lowMeaning: '30-49%: Prioritize rest and recovery. Yoga or a walk.',
        restMeaning: '0-29%: Rest day! Your body needs recovery.',
        proTip: 'Pro Tip',
        proTipText: 'Track sleep and workouts regularly for more accurate predictions. The more data, the better Nanouk understands your body!',
    },
};

export function ReadinessScoreCard() {
    const { language } = useLanguage();
    const t = content[language] || content.de;
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showDetails, setShowDetails] = useState(false);
    const [showExplanation, setShowExplanation] = useState(false);

    useEffect(() => {
        loadReadiness();
    }, []);

    const loadReadiness = async () => {
        try {
            const response = await getReadinessScore();
            setData(response.data);
            // Toast notification on auto-created recovery task (only once per page-load)
            if (response.data?.auto_recovery_task) {
                const { toast } = await import('sonner');
                toast.info(
                    (language === 'de' ? '🐺 Erholungs-Aufgabe von Nanouk: ' : '🐺 Recovery task from Nanouk: ') +
                    response.data.auto_recovery_task.title,
                    { duration: 6000 }
                );
            }
        } catch (error) {
            console.error('Failed to load readiness:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Card className="animate-pulse">
                <CardContent className="p-4 h-32" />
            </Card>
        );
    }

    if (!data) return null;

    const config = STATUS_CONFIG[data.status] || STATUS_CONFIG.moderate;
    const StatusIcon = config.icon;

    return (
        <>
            <Card className={`overflow-hidden ${config.bgColor} ${config.borderColor} border-2`}>
                <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                        {/* Nanouk State Image */}
                        <div className="relative flex-shrink-0">
                            <div className="w-14 h-14 rounded-2xl overflow-hidden shadow-lg">
                                <img 
                                    src={NANOUK_STATES[data.status] || NANOUK_STATES.moderate}
                                    alt="Nanouk"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        </div>

                        {/* Score and Message */}
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                                <span className={`text-2xl font-bold ${config.color}`}>{data.readiness_score}%</span>
                                <Badge className={`bg-gradient-to-r ${config.gradient} text-foreground border-0 text-[10px]`}>
                                    {config.label[language]}
                                </Badge>
                            </div>
                            <Progress value={data.readiness_score} className="h-1.5 mb-2" />
                            <p className="text-xs text-muted-foreground line-clamp-1">{data.nanouk_message}</p>
                        </div>

                        <Button variant="ghost" size="icon" onClick={() => setShowDetails(true)} className="flex-shrink-0">
                            <Info className="w-5 h-5" />
                        </Button>
                    </div>

                    {/* What to improve - motivational */}
                    <div className="mt-3 pt-3 border-t border-border/50 space-y-1.5">
                        {data.auto_recovery_task && (
                            <div className="flex items-start gap-2 p-2 -mt-1 mb-1 rounded-lg bg-teal-50 dark:bg-teal-950/30 border border-teal-200 dark:border-teal-900" data-testid="auto-recovery-task-banner">
                                <span className="text-base shrink-0">🐺</span>
                                <div className="flex-1 min-w-0">
                                    <p className="text-[11px] font-semibold text-teal-700 dark:text-teal-300 leading-tight">
                                        {language === 'de' ? 'Nanouk hat dir eine Erholungs-Aufgabe angelegt' : 'Nanouk added a recovery task for you'}
                                    </p>
                                    <p className="text-xs text-foreground/80 truncate">{data.auto_recovery_task.title}</p>
                                </div>
                            </div>
                        )}
                        {data.breakdown && (
                            <>
                                {data.breakdown.sleep_score < 60 && (
                                    <div className="flex items-center gap-2 text-xs">
                                        <Moon className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
                                        <span className="text-muted-foreground">
                                            {language === 'de' 
                                                ? `Schlaf verbessern (${data.breakdown.sleep_score}/100) — Frueh ins Bett?`
                                                : `Improve sleep (${data.breakdown.sleep_score}/100)`}
                                        </span>
                                    </div>
                                )}
                                {data.breakdown.activity_score < 60 && (
                                    <div className="flex items-center gap-2 text-xs">
                                        <Dumbbell className="w-3.5 h-3.5 text-orange-500 shrink-0" />
                                        <span className="text-muted-foreground">
                                            {language === 'de'
                                                ? `Mehr Bewegung (${data.breakdown.activity_score}/100) — Spaziergang?`
                                                : `More activity (${data.breakdown.activity_score}/100)`}
                                        </span>
                                    </div>
                                )}
                                {data.breakdown.consistency_score < 60 && (
                                    <div className="flex items-center gap-2 text-xs">
                                        <Flame className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                                        <span className="text-muted-foreground">
                                            {language === 'de'
                                                ? `Konstanz aufbauen (${data.breakdown.consistency_score}/100) — Streak halten!`
                                                : `Build consistency (${data.breakdown.consistency_score}/100)`}
                                        </span>
                                    </div>
                                )}
                                {data.readiness_score >= 70 && (
                                    <div className="flex items-center gap-2 text-xs">
                                        <Zap className="w-3.5 h-3.5 text-green-500 shrink-0" />
                                        <span className="text-green-600 dark:text-green-400 font-medium">
                                            {language === 'de' ? 'Guter Tag fuer ein Workout!' : 'Good day for a workout!'}
                                        </span>
                                    </div>
                                )}
                            </>
                        )}
                        {!data.breakdown && (
                            <div className="flex items-center gap-2 text-xs">
                                <Zap className={`w-3.5 h-3.5 ${config.color}`} />
                                <span className="text-muted-foreground">{data.recommended_activity}</span>
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* Details Dialog */}
            <Dialog open={showDetails} onOpenChange={setShowDetails}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <StatusIcon className={`w-5 h-5 ${config.color}`} />
                            {t.title} - {t.breakdown}
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-4">
                        {/* Main Score */}
                        <div className="text-center p-4 rounded-xl bg-gradient-to-br from-background to-secondary/30">
                            <div className="w-20 h-20 mx-auto mb-3 rounded-2xl overflow-hidden">
                                <img 
                                    src={NANOUK_STATES[data.status]}
                                    alt="Nanouk"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <div className={`text-5xl font-bold bg-gradient-to-r ${config.gradient} bg-clip-text text-transparent`}>
                                {data.readiness_score}%
                            </div>
                            <Badge className={`mt-2 bg-gradient-to-r ${config.gradient} text-foreground border-0`}>
                                {config.label[language]}
                            </Badge>
                        </div>

                        {/* Nanouk Message */}
                        <Card className={`${config.bgColor} ${config.borderColor}`}>
                            <CardContent className="p-3">
                                <p className="text-sm">{data.nanouk_message}</p>
                            </CardContent>
                        </Card>

                        {/* Breakdown */}
                        {data.breakdown && (
                            <div className="space-y-3">
                                <h4 className="font-semibold text-sm">{t.breakdown}</h4>
                                
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <Moon className="w-4 h-4 text-indigo-500" />
                                            <span className="text-sm">{t.sleepScore}</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Progress value={data.breakdown.sleep_score} className="w-20 h-2" />
                                            <span className="text-sm font-medium w-8">{data.breakdown.sleep_score}</span>
                                        </div>
                                    </div>

                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <Dumbbell className="w-4 h-4 text-orange-500" />
                                            <span className="text-sm">{t.activityScore}</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Progress value={data.breakdown.activity_score} className="w-20 h-2" />
                                            <span className="text-sm font-medium w-8">{data.breakdown.activity_score}</span>
                                        </div>
                                    </div>

                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <Flame className="w-4 h-4 text-red-500" />
                                            <span className="text-sm">{t.consistencyScore}</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Progress value={data.breakdown.consistency_score} className="w-20 h-2" />
                                            <span className="text-sm font-medium w-8">{data.breakdown.consistency_score}</span>
                                        </div>
                                    </div>

                                    {data.breakdown.recovery_penalty > 0 && (
                                        <div className="flex items-center justify-between text-orange-500">
                                            <div className="flex items-center gap-2">
                                                <Battery className="w-4 h-4" />
                                                <span className="text-sm">{t.recoveryPenalty}</span>
                                            </div>
                                            <span className="text-sm font-medium">-{data.breakdown.recovery_penalty}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Recommendation */}
                        <div className="p-3 rounded-xl bg-secondary/50">
                            <div className="flex items-center gap-2 mb-1">
                                <Zap className={`w-4 h-4 ${config.color}`} />
                                <span className="font-semibold text-sm">{t.recommendation}</span>
                            </div>
                            <p className="text-sm text-muted-foreground">{data.recommended_activity}</p>
                        </div>

                        <p className="text-xs text-center text-muted-foreground">{t.basedOn}</p>
                        
                        {/* Toggle Explanation */}
                        <Button 
                            variant="ghost" 
                            className="w-full text-sm"
                            onClick={() => setShowExplanation(!showExplanation)}
                        >
                            {showExplanation ? '▲' : '▼'} {t.whatIsTitle}
                        </Button>
                        
                        {showExplanation && (
                            <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                className="space-y-4 pt-2 border-t"
                            >
                                {/* What is Readiness Score */}
                                <div className="p-3 rounded-xl bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
                                    <p className="text-sm text-blue-800 dark:text-blue-200">
                                        💡 {t.whatIsDescription}
                                    </p>
                                </div>
                                
                                {/* How it's calculated */}
                                <div>
                                    <h4 className="font-semibold text-sm mb-2">{t.howCalculated}</h4>
                                    <div className="space-y-2 text-sm">
                                        <div className="flex items-start gap-2">
                                            <Moon className="w-4 h-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                                            <span className="text-muted-foreground">{t.sleepExplain}</span>
                                        </div>
                                        <div className="flex items-start gap-2">
                                            <Dumbbell className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
                                            <span className="text-muted-foreground">{t.activityExplain}</span>
                                        </div>
                                        <div className="flex items-start gap-2">
                                            <Flame className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                                            <span className="text-muted-foreground">{t.consistencyExplain}</span>
                                        </div>
                                        <div className="flex items-start gap-2">
                                            <Battery className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                                            <span className="text-muted-foreground">{t.recoveryExplain}</span>
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Status Meanings */}
                                <div>
                                    <h4 className="font-semibold text-sm mb-2">{t.statusMeanings}</h4>
                                    <div className="space-y-2 text-xs">
                                        <div className="flex items-center gap-2 p-2 rounded-lg bg-green-50 dark:bg-green-950/30">
                                            <BatteryCharging className="w-4 h-4 text-green-500" />
                                            <span>{t.peakMeaning}</span>
                                        </div>
                                        <div className="flex items-center gap-2 p-2 rounded-lg bg-lime-50 dark:bg-lime-950/30">
                                            <BatteryFull className="w-4 h-4 text-lime-500" />
                                            <span>{t.goodMeaning}</span>
                                        </div>
                                        <div className="flex items-center gap-2 p-2 rounded-lg bg-yellow-50 dark:bg-yellow-950/30">
                                            <BatteryMedium className="w-4 h-4 text-yellow-500" />
                                            <span>{t.moderateMeaning}</span>
                                        </div>
                                        <div className="flex items-center gap-2 p-2 rounded-lg bg-orange-50 dark:bg-orange-950/30">
                                            <BatteryLow className="w-4 h-4 text-orange-500" />
                                            <span>{t.lowMeaning}</span>
                                        </div>
                                        <div className="flex items-center gap-2 p-2 rounded-lg bg-red-50 dark:bg-red-950/30">
                                            <Battery className="w-4 h-4 text-red-500" />
                                            <span>{t.restMeaning}</span>
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Pro Tip */}
                                <div className="p-3 rounded-xl bg-violet-50 dark:bg-violet-950/30 border border-violet-200 dark:border-violet-800">
                                    <p className="text-xs font-semibold text-violet-700 dark:text-violet-300 mb-1">
                                        🐺 {t.proTip}
                                    </p>
                                    <p className="text-xs text-violet-600 dark:text-violet-400">
                                        {t.proTipText}
                                    </p>
                                </div>
                            </motion.div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}
